import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { BaseGroupType } from 'src/AngularJS/Models/group-type.model';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { GroupViewBySelectorCustom } from 'src/app/reusables/group-view-by-selector/group-view-by-selector.component';
import { GroupViewBy } from 'src/app/reusables/models/group-type.model';
import { CVApiService } from 'src/app/reusables/services/cv-api.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { AttributeType } from 'src/shared/models/attribute-selector.models';
import { IModuleCustomSettingSection, ModuleCustomSettingSectionType } from 'src/shared/models/module-custom-settings-section.model';
import { ModuleManager } from 'src/shared/models/module-manager';
import { IModuleManagerSettingsComponent, ModuleManagerDateRangeInfo, ModuleManagerGenericSettingsControls, ModuleManagerMetricInfo, ModuleManagerModuleSettings } from 'src/shared/models/module-manager/module-manager-settings.models';
import { FilterableField } from 'src/shared/models/pipeline-config/reporting-sets.models';
import SlideoutSettingsManager from 'src/shared/models/slideout-settings-manager';
import { MetricSelectorComponent } from '../../../reusables/metric-selector/metric-selector.component';
import { MetricType } from '../../../reusables/metric-selector/metric-selector.model';
import { MetricSelectorService } from '../../../reusables/metric-selector/metric-selector.service';
import { DateRangeSelectorService } from 'src/app/reusables/date-range-selector/date-range-selector.service';
import { MetricDynamicFiltersService } from 'src/app/reusables/metric-dynamic-filters/metric-dynamic-filters.service';
import { ReportingSetDynamicFiltersComponent } from 'src/app/reusables/reportingset-dynamic-filters/reportingset-dynamic-filters.component';

@Component({
    selector: 'cv-cxone-module-settings-modal',
    templateUrl: './cxone-module-settings-modal.component.html',
    styleUrls: ['./cxone-module-settings-modal.component.scss']
})
export class CxoneModuleSettingsModalComponent implements IModuleManagerSettingsComponent, OnInit, AfterViewInit, OnDestroy {
    @Input() moduleManager: ModuleManager;

    @ViewChildren('metricSelectorContainer', { read: ElementRef }) metricSelectorContainerQl: QueryList<ElementRef<HTMLDivElement>>;

    // ViewChildren for Custom Settings placements
    @ViewChild('beforeAll', { read: ViewContainerRef }) vcrBeforeAll: ViewContainerRef;
    @ViewChild('afterView', { read: ViewContainerRef }) vcrAfterView: ViewContainerRef;
    @ViewChild('afterAll', { read: ViewContainerRef }) vcrAfterAll: ViewContainerRef;
    @ViewChild('insideDate', { read: ViewContainerRef }) vcrInsideDate: ViewContainerRef;
    @ViewChild('afterDate', { read: ViewContainerRef }) vcrAfterDate: ViewContainerRef;
    @ViewChild('insideEmp', { read: ViewContainerRef }) vcrInsideEmp: ViewContainerRef;
    @ViewChild('afterEmp', { read: ViewContainerRef }) vcrAfterEmp: ViewContainerRef;
    @ViewChild('insideRec', { read: ViewContainerRef }) vcrInsideRec: ViewContainerRef;
    @ViewChild('afterRec', { read: ViewContainerRef }) vcrAfterRec: ViewContainerRef;
    @ViewChild('insideAtt', { read: ViewContainerRef }) vcrInsideAtt: ViewContainerRef;
    @ViewChild('afterAtt', { read: ViewContainerRef }) vcrAfterAtt: ViewContainerRef;
    @ViewChild('insideMetric', { read: ViewContainerRef }) vcrInsideMetric: ViewContainerRef;
    @ViewChild('afterMetric', { read: ViewContainerRef }) vcrAfterMetric: ViewContainerRef;

    settingsManager: SlideoutSettingsManager;
    settingsLoading = false;
    genericSettingsLoading = false;
    genericSettingsLoaded = false;
    isValid = true;
    isDateRangeValid = true;
    isModuleNameValid = true;
    invalidCharsRegex = /^([^\\\\\/\!\+\<\>\?\#\&\,\%])*$/;
    isPatternValid = true;
    maxDateRangeText = 'Date range larger than the maximum date range of {0} days.';
    textLimit: number;
    textLimitMessage = '';
    editName: string;
    hidePlural = false;
    isSingleAttrSelector = false;
    customViewBy: GroupViewBySelectorCustom[] = [];
    qeFilterableAttributes: { [FieldName: string]: FilterableField };

    private metricSelectorContainerEl: ElementRef<HTMLDivElement>;
    private metricSelectorHtml = '';
    private _subscriptions: Subscription[] = [];

    // Keep these sorted for easier merge conflict resolution
    private dateRangeChangeExceptions_Acd = [
        2, // CXone ACD KPI
        16, // CXone Leaderboard
        110, // CXone ACD Agent State Summary
        111, // CXone ACD Metrics Interval
        112, // CXone ACD Metrics Summary
        131, // Report
        132, // CXone KPI Trend
        133, // CXone Coaching Status
        134, // CXone Gauge
        151, // CXone Dispositions
    ];

    hideSettingsDatePicker = false;
    groupViewBy: GroupViewBy = { GroupLevel: 0, GroupType: 0 };

    constructor(
        private apiService: CVApiService,
        private modal: NgbActiveModal,
        private attributeSelectorService: AttributeSelectorService,
        public metricSelectorService: MetricSelectorService,
        private localizationService: CVLocalizationService,
        private zone: NgZone,
        private dateRangeSelectorService: DateRangeSelectorService,
    ) { }

    get module() {
        return this.moduleManager?.getModuleInfo()?.DTO;
    }

    get stateKey() {
        return this.moduleManager?.getModuleKey();
    }

    ngOnInit(): void {
        const moduleOptions = this.moduleManager.moduleOptions;
        this.moduleManager.moduleOptions.editName = moduleOptions.isDefaultName ? moduleOptions.name : moduleOptions.editName;
        this.isSingleAttrSelector = this.moduleManager.useSingleAttrSelector();
        this.initializeSettingsManager();

        // handle jQuery event for metric selector because Angular can only handle native events
        $(document).on('event_SelectedMetricsChanged', this.onSelectedMetricsChangedHandler);

        if (this.module?.SettingAttSelector)
            this.initializeAttributesViewBySelector();
        this.editName = this.moduleManager.moduleOptions.editName;
        this.characterLimit(this.editName);
        this.updateDatePicker(this.settingsManager?.selectedMetrics);

        // Subscribe to the selected group view by changes to limit the filterable selection
        this.settingsManager.onSelectedGroupViewByChanged.subscribe(s => this.onSelectedGroupViewByChanged(s));
    }

    ngAfterViewInit(): void {
        this.initializeMetricSelectorContainer(this.metricSelectorContainerQl);
        this.metricSelectorContainerQl.changes.subscribe((next: QueryList<ElementRef<HTMLDivElement>>) => {
            this.initializeMetricSelectorContainer(next);
        });

        this._subscriptions.push(this.settingsManager.onPlaceCustomSettings.subscribe(
            component => this.placeCustomSettingsSection(component)
        ));

        this.moduleManager.slideoutSettingsManager.next(this.settingsManager);

        this.groupViewBy.GroupLevel = this.settingsManager.selectedLevel;
        this.groupViewBy.GroupType = this.settingsManager.selectedToggle;
    }

    ngOnDestroy(): void {
        $(document).off('event_SelectedMetricsChanged', this.onSelectedMetricsChangedHandler);
        this._subscriptions.forEach(s => s?.unsubscribe());
    }

    onSelectedGroupViewByChanged(s) {
        this.groupViewBy = s;

        this.limitViewByFilterable();
    }

    /** Limits the filterable that is set to the view by. Also removes the limit on filterables that have been previously overridden */
    limitViewByFilterable() {
        const filterableGroupingDTO = this.settingsManager.filterableGroupingDTO;

        if (this.groupViewBy.GroupType === 4 &&  // 4 is Filterables. BaseGroupType doesn't have Filterables in it for some reason right now. TODO: Fix this
            filterableGroupingDTO != null &&
            !!this.moduleManager.filterablesComponent
        ) {
            const maxSelection = this.moduleManager.container.moduleInfo.DTO.SettingViewByLimitsFilters;
            if (maxSelection > 0) { // Limit exists, make sure to check if the limit is exceeded and reset the matching filterable                
                
                const filterableKey = filterableGroupingDTO.ValueField; // The field that is set to the view by
                const selectedFilterableCount = this.moduleManager.filterablesComponent?.selectedDynamicFilterableFields?.[filterableKey]?.length ?? 0;
                
                // If the count of view by'ed filterable has more items than maxSelection -> Slice everything by the first X items where X is maxSelection
                if (selectedFilterableCount > maxSelection) {
                    this.moduleManager.filterablesComponent.selectedDynamicFilterableFields[filterableKey] =
                        this.moduleManager.filterablesComponent.selectedDynamicFilterableFields[filterableKey].sort((a, b) => a.DisplayValue.localeCompare(b.DisplayValue)).slice(0, maxSelection);
                }

                // Reset the limit on all filterables and then set the limit on the view by filterable
                this.moduleManager.filterablesComponent.restoreAllMaxSelectionOverrides();
                this.moduleManager.filterablesComponent.overrideMaxSelection(filterableKey, maxSelection);

                // Saving state
                this.moduleManager.filterablesComponent.stateValues.Values[filterableKey] = [];
                this.moduleManager.filterablesComponent.selectedDynamicFilterableFields[filterableKey].forEach(element => {
                    this.moduleManager.filterablesComponent.stateValues.Values[filterableKey].push(element.Value);
                });
            }
        }
    }

    //here tap into isiah's endpoint to get the filterables and then populate the customViewBy with filterables before the attributes categories.
    async onFilterablesLoaded(filterableFields: { [FieldName: string]: FilterableField }){
        for (const filterable in filterableFields) {
            if (
                filterableFields[filterable].CanViewBy &&
                filterableFields[filterable].ValueSource == 1
            ) {
                const newItem: GroupViewBySelectorCustom = {
                    id: `${BaseGroupType.Filterables}_${filterableFields[filterable].DisplayName}`,
                    type: BaseGroupType.Filterables,
                    value: BaseGroupType.Filterables,
                    display: filterableFields[filterable].DisplayName,
                    filterableGroupingDTO: {
                        ValueField: filterable,
                        ValueDisplayField: filterableFields[filterable].ValueDisplayField,
                        ValueSourceReportingSet: filterableFields[filterable].ValueSourceReportingSet,
                        FilterableDisplayName: filterableFields[filterable].DisplayName,
                    }
                };
        
                // Check if the item already exists in customViewBy
                const exists = this.customViewBy.some(item => item.id === newItem.id);
        
                if (!exists) {
                    this.customViewBy.push(newItem);
                }
            }
        }
        //this line is to fire the ngOnChanges in the viewby component. Don't ask me why .push doesn't do that
        this.customViewBy = [...this.customViewBy];

        // If we haven't already subscribed to the filterables component, do so now
        if (!this._subscriptions.some(s => s.closed)) {
            this._subscriptions.push(this.moduleManager.onFilterablesComponentSet.subscribe(() => this.limitViewByFilterable()));
        }
    }

    async initializeAttributesViewBySelector() {
        const categories = await this.attributeSelectorService.loadCategories(AttributeType.Data);
        // sort by category name alphabetically
        categories.AttributeCategoryModels.sort((a, b) => a.CategoryName.localeCompare(b.CategoryName));
        for (const category of categories.AttributeCategoryModels) {
            this.attributeSelectorService.loadValues(category.CategoryID, AttributeType.Data);
            this.customViewBy.push({
                id: `${BaseGroupType.DataAttribute}_${category.CategoryID}`,
                type: BaseGroupType.DataAttribute,
                value: category.CategoryID,
                display: category.CategoryName,
            });
        }
    }

    initializeSettingsManager() {
        if (this.settingsManager)
            return;

        if (this.moduleManager.slideoutSettingsManager.getValue()) {
            this.settingsManager = this.moduleManager.slideoutSettingsManager.getValue();
        } else if (this.settingsManager == null) {
            this.settingsManager = new SlideoutSettingsManager(this.attributeSelectorService, this.module);
        }
    }

    initializeMetricSelectorContainer(container: QueryList<ElementRef<HTMLDivElement>>) {
        this.metricSelectorContainerEl = container.first;
        if (this.metricSelectorContainerEl && this.metricSelectorHtml)
            this.metricSelectorContainerEl.nativeElement.innerHTML = this.metricSelectorHtml;
    }

    setGenericSettingsControlSettings(controlSettings: ModuleManagerModuleSettings) {
        this.initializeSettingsManager();
        this.settingsManager.setSettings(controlSettings);
        if (this.module.SettingMetricSelector)
            this.settingsManager.updateSelectedMetricsText(false);
    }

    setGenericSettingsHtml(data: ModuleManagerGenericSettingsControls): void {
        this.setGenericSettingsControlSettings(data.controlSettings);
        this.settingsManager.availableDateRanges = data.availableDateRanges.map(dr => ModuleManagerDateRangeInfo.fromObject(dr));
    }

    placeCustomSettingsSection(component: IModuleCustomSettingSection) {
        if (component.type !== ModuleCustomSettingSectionType.CxOne)
            return;

        const $customSection = component.getNativeElement();
        let targetPlaceholder: ViewContainerRef;

        // determine which ViewContainerRef to place the component after
        if (component.after != null) {
            switch (component.after) {
                case 'date': targetPlaceholder = this.vcrAfterDate; break;
                case 'emp': targetPlaceholder = this.vcrAfterEmp; break;
                case 'rec': targetPlaceholder = this.vcrAfterRec; break;
                case 'att': targetPlaceholder = this.vcrAfterAtt; break;
                case 'metric': targetPlaceholder = this.vcrAfterMetric; break;
                case 'all': targetPlaceholder = this.vcrAfterAll; break;
                case 'view': targetPlaceholder = this.vcrAfterView; break;
                default: targetPlaceholder = this.vcrBeforeAll; break;
            }
        } else if (component.inside != null) {
            switch (component.inside) {
                case 'date': targetPlaceholder = this.vcrInsideDate; break;
                case 'emp': targetPlaceholder = this.vcrInsideEmp; break;
                case 'rec': targetPlaceholder = this.vcrInsideRec; break;
                case 'att': targetPlaceholder = this.vcrInsideAtt; break;
                case 'metric': targetPlaceholder = this.vcrInsideMetric; break;
                default: targetPlaceholder = this.vcrBeforeAll; break;
            }
        } else {
            targetPlaceholder = this.vcrBeforeAll;
        }

        // place the custom section inside of the target container
        const $placeholderElement: Node = targetPlaceholder?.element?.nativeElement;
        $placeholderElement?.parentNode?.insertBefore($customSection, $placeholderElement);
    }

    onSelectedMetricsChanged(e) {
        if (!e.result && e.pageName === `${this.stateKey}_temp`) {
            this.zone.run(() => {
                this.settingsManager.selectedMetrics = e.selectedMetrics?.map(metricId => ({
                    Id: metricId,
                    Abbreviation: e.metricNames[metricId].trim(),
                    Name: e.metricFullNames[metricId],
                    ConversionType: parseInt(e.mCT[metricId], 10),
                    MetricType: parseInt(e.metricTypes[metricId], 10),
                }));
                this.settingsManager.selectedMetricIDs = e.selectedMetrics;

                this.settingsManager.updateSelectedMetricsText(false);
            });
        }
    }

    openMetricSelector() {
        const curSelectedMetrics = this.settingsManager.selectedMetrics;
        const metricSelector = this.metricSelectorService.openModal(MetricSelectorComponent, this.moduleManager, curSelectedMetrics, true);
        const newSelectedMetrics = [];
        metricSelector.result.then((metricSelectorResult) => {
            if (metricSelectorResult) {
                metricSelectorResult?.forEach(m => {
                    newSelectedMetrics.push({
                        Id: m.MetricID,
                        Abbreviation: m.Abbreviation,
                        Name: m.MetricName,
                        ConversionType: m.ConversionType,
                        MetricType: m.MetricType
                    });
                });
                this.settingsManager.selectedMetrics = newSelectedMetrics;
                this.settingsManager.selectedMetricIDs = newSelectedMetrics?.map(m => m.Id);
                this.settingsManager.updateSelectedMetricsText();
                this.qeFilterableAttributes = this.metricSelectorService.getQEFilterableAttributes(metricSelectorResult);
                this.updateDatePicker(this.settingsManager.selectedMetrics);
            }
        });
    }

    async updateDatePicker(selectedMetrics: ModuleManagerMetricInfo[]) {
        let hasCurrentMetrics = false;
        let hasHistoricalMetrics = false;

        // If the widget doesn't have a metric selector, show the date picker by default.
        // If it does, use the selected metrics to determine if we need to hide it.
        if (!this.module?.SettingMetricSelector) {
            this.hideSettingsDatePicker = false;
        }
        else if (selectedMetrics?.length > 0) {
            selectedMetrics.forEach(metric => {
                if (metric.MetricType == MetricType.CurrentStatesMetricData || metric.MetricType == MetricType.Snapshot) {
                    hasCurrentMetrics = true;
                } else {
                    hasHistoricalMetrics = true;
                }
            })

            this.hideSettingsDatePicker = hasCurrentMetrics && !hasHistoricalMetrics
        }
        else {
            this.hideSettingsDatePicker = true
        }

        if (this.hideSettingsDatePicker) {
            // Reset DateRange selection to default "Today" on RT metric
            const today = this.settingsManager.availableDateRanges.find((daterange) => daterange.name == "Today");
            this.settingsManager.selectedDateRange = today;
            await this.dateRangeSelectorService.saveDateRange(`${this.stateKey}_temp`, this.settingsManager.selectedDateRange.name);
        }

        this.moduleManager.moduleSettings.hideDatePicker = this.hideSettingsDatePicker;
    }

    async removeMetric(metric: ModuleManagerMetricInfo) {
        const removeResponse = await this.apiService.delete<boolean>(`/api/v1/metric-selector/${this.stateKey}_temp/${metric.Id}`);
        if (removeResponse.data === true) {
            // update selected metrics text
            this.settingsManager.selectedMetrics = this.settingsManager.selectedMetrics.filter(m => m.Id !== metric.Id);
            this.settingsManager.updateSelectedMetricsText();

            const metricSelectorModuleEl = this.metricSelectorContainerEl?.nativeElement?.querySelector('.metricSelectorModule');
            metricSelectorModuleEl?.setAttribute("pagename", `${this.stateKey}_temp`);
            // remove from metric selector
            const removeMetricBtn: HTMLButtonElement = this.metricSelectorContainerEl?.nativeElement?.querySelector(
                `.selectedMetric[mid='${metric.Id}'] .removeMetric`
            );
            removeMetricBtn?.click();

            const selectedMetrics = this.settingsManager.selectedMetrics.filter((sm) => sm.Id !== metric.Id);

            const eventObj = {
                pageName: `${this.stateKey}_temp`,
                selectedMetrics: new Array<number>(),
                metricNames: {},
                metricFullNames: {},
                mCT: {},
                metricTypes: {},
                result: false
            };

            selectedMetrics.forEach((sm) => {
                eventObj.selectedMetrics.push(sm.Id);
                eventObj.metricNames[sm.Id] = sm.Abbreviation;
                eventObj.metricFullNames[sm.Id] = sm.Name;
                eventObj.mCT[sm.Id] = sm.ConversionType;
                eventObj.metricTypes[sm.Id] = sm.MetricType;
            });

            $(document).trigger(new jQuery.Event("event_SelectedMetricsChanged", eventObj));

            this.updateDatePicker(this.settingsManager.selectedMetrics);
        }
    }

    async validateDateRange() {
        const numDays = this.settingsManager.selectedDateRange.numberOfDays;
        this.isDateRangeValid = numDays <= this.moduleManager.maxDateRange;
        if (!this.isDateRangeValid) {
            const fallback = 'Date range larger than the maximum date range of {0} days.';
            const maxDateRangeTemplate = await this.localizationService.GetLocalText('Module', 'MaxDateRange', fallback);
            this.maxDateRangeText = this.localizationService.Format(maxDateRangeTemplate, this.moduleManager.maxDateRange);
        }
        this.updateIsValid();
    }

    isModuleNameEmpty() {
        return this.editName.trim() !== "" && this.editName !== null;
    }

    updateIsValid() {
        this.isValid = this.isDateRangeValid;
    }

    applySettings() {
        if (!this.isValid) {
            return;
        }

        // allow settings to request waits
        this.settingsManager.waitBeforeApplying = [];
        this.settingsManager.beforeSettingsApplied.next();

        const waitBeforeSaving: Promise<any>[] = [...this.settingsManager.waitBeforeApplying];
        let waitPromise: Promise<any> = null;
        if (waitBeforeSaving.length > 0)
            waitPromise = Promise.all(waitBeforeSaving);

        this.modal.close(waitPromise);
    }

    cancelSettings() {
        this.cancel();
    }

    cancel() {
        this.settingsManager?.onSettingsCancelled.next();
        this.moduleManager.moduleEvents.onSettingsCancelled?.handle();
        this.modal.dismiss();
    }

    save() {
        this.applySettings();
    }

    log(e: any) {
        console.log('dateRangeChanged', e);
    }

    isDateChangeAllowed(): boolean {
        const moduleInfo = this.moduleManager?.getModuleInfo();

        // If CXone's ACD group...
        if (moduleInfo?.ModuleGroup === "CXone_ACD") {
            // If in the exceptions Acd list, allow date range change
            if (this.dateRangeChangeExceptions_Acd.includes(moduleInfo.ModuleID))
                return true;

            // Not in the exception list for ACD. Date range picker shouldn't allow change.
            return false;
        }

        return true;
    }

    isDayOfWeekAllowed(): boolean {
        const moduleInfo = this.moduleManager?.getModuleInfo();

        // If CXone's ACD group...
        if (moduleInfo?.ModuleGroup === "CXone_ACD") {
            // No exception list, currently
            return false;
        }
        else
            return true;
    }

    characterLimit(value: string) {
        this.textLimit = 35 - value.length;
        if (this.textLimit === 1) {
            this.hidePlural = true;
        }
        else {
            this.hidePlural = false;
        }
        const editName = value.trim();
        if (!this.invalidCharsRegex.test(editName.toLocaleLowerCase()) && !editName.toLocaleLowerCase().match(this.invalidCharsRegex)){
            this.isPatternValid = false;
        }else {
            this.isPatternValid = true;
        }
        this.isValid = editName.length === 0 || editName === '' ? this.isValid = false : this.isValid = true;
    }

    getTextLimitMessage() {
        if (this.hidePlural)
            this.textLimitMessage = this.localizationService.Format(
                this.localizationService.getCachedText('CXOne_Modal', 'Character_Limit_Singular', '{0} character remaining'),
                this.textLimit
            );
        else
            this.textLimitMessage = this.localizationService.Format(
                this.localizationService.getCachedText('CXOne_Modal', 'Character_Limit', '{0} characters remaining'),
                this.textLimit
            );
        return this.textLimitMessage;
    }

    private onSelectedMetricsChangedHandler = (e: any) => this.onSelectedMetricsChanged(e);
}
